import { useFormik } from "formik";
import { useCustomerAccount } from "../../AccountProvider";
import React, { useState, useContext, useRef } from "react";
import { LoadingContext } from "../../LoadingProvider";
import { useCommonContext } from "../../CommonProvider";
import { MESSAGE } from "../../../../constants";
import { isEmpty } from "lodash";
import { CSSTransition } from "react-transition-group";
import PasswordRequirementsOutline from "../../../../components/common/PasswordRequirementOutline/PasswordRequirementsOutline";
import { FieldError, PasswordField } from "../../../common/fields";
import { MESSAGE_TYPE } from "../../../../constants/messages";
import { changePassword, getOtpOptions } from "../../../../api/custAccountApi";
import { getErrorCode, handleApiError } from "../../../../utils/errors"
import { getPasswordQualifications, getPasswordStrengthScore, isPasswordFieldValid } from "../../../../utils/validators"
import analytics from "../../../../utils/analytics";
import { isFeatureSwitchEnabled, FEATURE_SWITCHES, isUserB2B, isNativeApp } from "../../../../utils"
import { PasswordEditOtpModal } from "./PasswordEditOtpModal";
import {Button, ButtonGroup} from "@one-thd/sui-atomic-components";

const PasswordEdit = (props) => {
  const {onDoneEditing, email = ""} = props;
  const context = useCustomerAccount();
  const {account, setStale} = context;
  const { showLoading, hideLoading } = useContext(LoadingContext);
  const { addAlert } = useCommonContext();

  //useState hooks
  const [isNewPasswordFocus, setIsNewPasswordFocus] = useState(false);
  // 1 time flag to prevent new password  guidelines from
  // displaying until user has focused on form
  const [hasNewPasswordBeenFocused, setHasNewPasswordBeenFocused] = useState(false);
  const [isPasswordLengthValid, setIsPasswordLengthValid] = useState(false);
  const [isPasswordUppercase, setIsPasswordUppercase] = useState(false);
  const [isPasswordLowercase, setIsPasswordLowercase] = useState(false);
  const [isPasswordNumber, setIsPasswordNumber] = useState(false);
  const [isPasswordSpecialCharacter, setIsPasswordSpecialCharacter] = useState(false);
  const [passwordStrengthScore, setPasswordStrengthScore] = useState(0);
  // state for OTP-based password update
  const [showPasswordEditOtpModal, setShowPasswordEditOtpModal] = useState(false);
  const newPassword = useRef("");
  const otpOptions = useRef([]);
  const otpOptionsEncryptedPayload = useRef(null);

  const closePasswordEditOtpModal = () => setShowPasswordEditOtpModal(false);

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
    },
    validate: isPasswordFieldValid(email),
    onSubmit: (values, actions) => {
      showLoading();
      if (isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_OTP_PASSWORD_UPDATE)) {
        // initiate password edit otp flow by calling otp options
        getOtpOptions(email).then(({ response, error }) => {
          hideLoading();
          if (response) {
            otpOptions.current = response.data?.otpOptions;
            otpOptionsEncryptedPayload.current = response.data?.encryptedPayload;
            newPassword.current = values.newPassword;
            setShowPasswordEditOtpModal(true);
          } else {
            const errorMessage = handleApiError(error, "get password otp options");
            actions.setStatus([errorMessage]);
          }
        });
      } else {
        const requestParams = { newPassword: values.newPassword, currentPassword: values.currentPassword, account }
        changePassword({ ...requestParams }).then(({ response, error }) => {
          if (response) {
            onDoneEditing();
            setStale();
            analytics.postSettingsSubmissionEvent("security:password edit success");
            addAlert(MESSAGE_TYPE.SUCCESS, MESSAGE.PASSWORD_CHANGED);
          } else {
            const errorCode = getErrorCode(error);
            const errorMessage = handleApiError(error, "edit password");

            if (errorCode === "AUTH_ERR_102" || errorCode === "UREG_ERR_103") {
              actions.setFieldError("currentPassword", [errorMessage]);
            } else if (errorCode === "AUTH_ERR_104" || errorCode === "AUTH_ERR_116" || errorCode === "AUTH_ERR_130" || errorCode === "AUTH_ERR_116A" || errorCode === "UREG_ERR_168") {
              actions.setFieldError("newPassword", [errorMessage]);
            } else if (errorCode === "AUTH_ERR_129") {
              actions.setStatus([MESSAGE.PASSWORD_EXPIRED_FOR_INACTIVE_USERS]);
            } else {
              actions.setStatus([errorMessage]);
            }
          }
          hideLoading();
        });
      }
    }
  });

  const currentPasswordFieldAutomationId = "login-information-password-form--edit-password-field-current";
  const newPasswordFieldAutomationId = "login-information-password-form--edit-password-field-new";

  const cancelButtonAutomationId = "login-information-password-form--button-cancel";
  const submitButtonAutomationId = "login-information-password-form--button-submit";

  const onBlur = (e) => {
    if (e.relatedTarget?.type !== "button" && !showPasswordEditOtpModal) {
      formik.handleBlur(e);
    }
  }

  const onNewPasswordBlur = (e) => {
    if (!showPasswordEditOtpModal) {
      formik.handleBlur(e);
      setIsNewPasswordFocus(false);
    }
  }

  const onChange = (e) => {
    formik.handleChange(e);
    const password = e.target.value;
    const passwordQualifications = getPasswordQualifications(password);
    const passwordMatchesEmail = password === email && password !== "";
    setIsPasswordLengthValid(passwordMatchesEmail?false:passwordQualifications.MIN_LENGTH);
    setIsPasswordUppercase(passwordMatchesEmail?false:passwordQualifications.UPPER_CASE);
    setIsPasswordLowercase(passwordMatchesEmail?false:passwordQualifications.LOWER_CASE);
    setIsPasswordNumber(passwordMatchesEmail?false:passwordQualifications.NUMBER);
    setIsPasswordSpecialCharacter(passwordMatchesEmail?false:passwordQualifications.NON_ALPHA);
    setPasswordStrengthScore(passwordMatchesEmail?1:getPasswordStrengthScore(password));
  }

  const onFocus = () => {
    setIsNewPasswordFocus(true);
    setHasNewPasswordBeenFocused(true);
  }

  const passwordEditOtpModalProps = {
    email,
    otpOptions: otpOptions.current,
    otpOptionsEncryptedPayload: otpOptionsEncryptedPayload.current,
    newPassword: newPassword.current,
    closeModal: closePasswordEditOtpModal,
    onSuccess: onDoneEditing,
  };

  return (
    <>
      <div className="col__12-12 u--paddingNone security-info-row">
        <form className="col__12-12 u--paddingNone-left u--paddingNone-right" onSubmit={formik.handleSubmit}>
          <div className="security-info-column sui-grid sui-gap-2">
            {!isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_OTP_PASSWORD_UPDATE) && <div className="col__12-12 no-padding-sides">
              <PasswordField
                id="currentPassword"
                name="currentPassword"
                autoComplete="current-password"
                dataAutomationId={currentPasswordFieldAutomationId}
                type="password"
                label="Current Password"
                valid={!(formik.touched.currentPassword && !isEmpty(formik.errors.currentPassword))}
                onChange={formik.handleChange}
                onBlur={onBlur}
                value={formik.values.currentPassword}
                autoFocus={ !isNativeApp() } // eslint-disable-line
                >

                {formik.touched.currentPassword && !isEmpty(formik.errors?.currentPassword) && <FieldError name="currentPassword" errors={formik.errors?.currentPassword} />}
              </PasswordField>
            </div>}

            <div className="col__12-12">
              <PasswordField
                id="newPassword"
                name="newPassword"
                autoComplete="new-password"
                dataAutomationId={newPasswordFieldAutomationId}
                type="password"
                label="New Password"
                valid={!(formik.touched?.newPassword && !isEmpty(formik.errors?.newPassword))}
                onChange={onChange}
                onBlur={onNewPasswordBlur}
                onFocus={onFocus}
                value={formik.values.newPassword}
                autoFocus={!isNativeApp() && isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_OTP_PASSWORD_UPDATE)} // eslint-disable-line
                >

                {formik.touched.newPassword && !isEmpty(formik.errors?.newPassword) && <FieldError name="newPassword" errors={formik.errors?.newPassword} />}
              </PasswordField>
            </div>

            { (formik.touched.newPassword && formik.touched.currentPassword) && !isEmpty(formik.status) &&
                  <div className="col__12-12 no-padding-sides form-input--error">
                    <FieldError name="password" errors={formik.status}/>
                  </div>
            }

            <CSSTransition
              in={ hasNewPasswordBeenFocused && (isNewPasswordFocus || !isEmpty(formik.errors?.newPassword) ) }
              timeout={0}
              classNames="error"
              unmountOnExit
            >

              <PasswordRequirementsOutline
                password={{value: formik.values.newPassword}}
                isPasswordLengthValid={isPasswordLengthValid}
                isPasswordUppercase={isPasswordUppercase}
                isPasswordLowercase={isPasswordLowercase}
                isPasswordNumber={isPasswordNumber}
                isPasswordSpecialCharacter={isPasswordSpecialCharacter}
                passwordStrengthScore={passwordStrengthScore}
              />
            </CSSTransition>

            { !isUserB2B() && (
                <p className="new-password-reset-warning">
                    Resetting your password will sign you out of all other devices.
                </p>
            )}


            <ButtonGroup responsive>
              <Button variant='primary' type="submit" disabled={!formik.isValid} data-automation-id={submitButtonAutomationId}>
                Save Changes
              </Button>
              <Button onClick={onDoneEditing} data-automation-id={cancelButtonAutomationId}>
                Cancel
              </Button>
            </ButtonGroup>
          </div>
        </form>
        { showPasswordEditOtpModal &&
          <PasswordEditOtpModal {...passwordEditOtpModalProps} />
        }
      </div>
    </>
  );
}

export default PasswordEdit;